<script setup>
import { computed, getCurrentInstance } from 'vue';
import { useMainStore } from '@/stores/index';

const props = defineProps({
  document: {
    type: Object,
    required: true
  }
});

const Store = useMainStore();
const instance = getCurrentInstance();

const getUserAvatar = (path) => {
  if (instance?.appContext?.config?.globalProperties?.$userAvatar) {
    return instance.appContext.config.globalProperties.$userAvatar(path);
  }
  if (!path) return '/img/default-avatar.png';
  if (path.startsWith('http')) return path;
  return `/storage/${path}`;
};

const authorDepartment = computed(() => {
  const deptId = props.document?.author?.department_id;
  if (!deptId || !Store.departamentsAll) return props.document?.author?.department || null;
  return Store.departamentsAll.find((d) => d.id === deptId) || props.document?.author?.department || null;
});

const toUsers = computed(() => props.document?.to_users || []);
const toDepartments = computed(() => props.document?.to_departments || []);
const totalDestinations = computed(() => toUsers.value.length + toDepartments.value.length);
</script>

<template>
  <div class="sector-flow-wrapper d-inline-flex align-items-center gap-1 mt-1">
    <div
      v-if="authorDepartment"
      class="badge-circle-sector custom-tooltip d-flex align-items-center justify-content-center"
      :style="{ backgroundColor: authorDepartment.assets || '#6f42c1' }"
      :data-tooltip="authorDepartment.title || authorDepartment.name"
    >
      <span class="text-white font-docs fw-bold font-size-10">{{ authorDepartment.acronym }}</span>
    </div>

    <font-awesome-icon
      v-if="authorDepartment && totalDestinations > 0"
      :icon="['fas', 'arrow-right']"
      class="flow-arrow text-muted mx-1"
    />

    <div v-if="totalDestinations > 0" class="d-inline-flex align-items-center">
      <div
        v-for="u in toUsers.slice(0, 2)"
        :key="u.user?.id || u.id"
        class="custom-tooltip d-inline-flex align-items-center"
        :data-tooltip="u.user?.name"
      >
        <img
          :src="getUserAvatar(u.user?.path_image)"
          :alt="u.user?.name"
          class="badge-avatar-img"
        />
      </div>
      <div
        v-for="dep in toDepartments.slice(0, 2 - toUsers.slice(0, 2).length)"
        :key="dep.department?.id || dep.id"
        class="badge-circle-sector custom-tooltip d-flex align-items-center justify-content-center"
        :style="{ backgroundColor: dep.department?.assets || '#e83e8c' }"
        :data-tooltip="dep.department?.title"
      >
        <span class="text-white font-docs fw-bold font-size-10">{{ dep.department?.acronym }}</span>
      </div>
      <div
        v-if="totalDestinations > 2"
        class="badge-more-circle d-flex align-items-center justify-content-center"
      >
        <span class="font-docs fw-bold font-size-10">+{{ totalDestinations - 2 }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sector-flow-wrapper {
  vertical-align: middle;
}

.badge-circle-sector {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  cursor: pointer;
}

.badge-avatar-img {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.badge-more-circle {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: #e7f1ff;
  border: 1px solid #3d8bfd;
  color: #3d8bfd;
  flex-shrink: 0;
}

.flow-arrow {
  font-size: 10px;
}

.font-size-10 {
  font-size: 10px;
}

.custom-tooltip {
  position: relative;
  display: inline-flex;
}

.custom-tooltip::before {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  background-color: #212529;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease-in-out;
  z-index: 100;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.custom-tooltip::after {
  content: '';
  position: absolute;
  bottom: 105%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: #212529 transparent transparent transparent;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease-in-out;
  z-index: 100;
}

.custom-tooltip:hover::before,
.custom-tooltip:hover::after {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}
</style>